/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';

import { Button } from '@coursera/cds-core';
import { FlagIcon } from '@coursera/cds-icons';

import { isVivaExamEnabled } from 'bundles/academic-integrity/lib/epics';
import { PlagiarismContext } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismContext';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import PlagiarismDrawer from 'bundles/grading/components/staff-graded-assignments/plagiarism/PlagiarismDrawer';

import _t from 'i18n!nls/grading';

const styles = {
  integrityFlag: () => css`
    margin-top: var(--cds-spacing-150);
    position: relative;
  `,
  badge: () => css`
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: var(--cds-color-feedback-error);
    color: var(--cds-color-neutral-primary-invert);
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 1000px;
    font-size: 10px;
    font-weight: bold;
    line-height: 1;
    min-width: 18px;
    height: 18px;
  `,
};

type IntegrityFlagProps = {
  submitterId: string;
  submitterName?: string;
};

const IntegrityFlag = ({ submitterId, submitterName }: IntegrityFlagProps) => {
  const {
    pageData: { courseId, currentItemId },
  } = useContext(SGAPageContext);
  const { cheatingCaseStaffComments, activeIntegrityFlagCount, setActiveIntegrityFlagCount } =
    useContext(PlagiarismContext);

  useEffect(() => {
    const hasFlag = Boolean(cheatingCaseStaffComments && cheatingCaseStaffComments.length);

    if (hasFlag) {
      // if learner has a cheating case, increment the active integrity flag count
      setActiveIntegrityFlagCount((prevCount) => {
        return prevCount < 2 ? prevCount + 1 : prevCount;
      });
    }
  }, [cheatingCaseStaffComments, setActiveIntegrityFlagCount]);
  const vivaExamEnabled = isVivaExamEnabled(courseId, currentItemId);

  const [isPlagiarismDrawerOpen, setPlagiarismDrawerOpen] = useState(false);

  const togglePlagiarismDrawer = () => {
    setPlagiarismDrawerOpen((prevState) => !prevState);
  };

  if (!vivaExamEnabled) {
    return null;
  }

  return (
    <Fragment>
      <Button
        iconPosition="before"
        size="small"
        variant="ghost"
        icon={<FlagIcon size="small" aria-hidden="true" />}
        onClick={togglePlagiarismDrawer}
        css={styles.integrityFlag}
      >
        {_t('View integrity flags')}
        {(activeIntegrityFlagCount || 0) > 0 && <div css={styles.badge}>{activeIntegrityFlagCount}</div>}
      </Button>
      <PlagiarismDrawer
        key={`plagiarism-${submitterId}`}
        isOpen={isPlagiarismDrawerOpen}
        onClose={() => setPlagiarismDrawerOpen(false)}
        submitterId={submitterId}
        submitterName={submitterName}
      />
    </Fragment>
  );
};

export default IntegrityFlag;
